import React, { useState, useEffect, useRef, useContext } from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { API_URL_ticketPriority } from "constants/api";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { postData, putData } from "actions";
import { Tooltip } from "react-tooltip";
import { AuthContext } from "context/AuthContext";

const FormPriorities = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      priority: location.state ? location.state.item.priority : "",
      priority_desc: location.state ? location.state.item.priority_desc : "",
      priority_color: location.state
        ? location.state.item.priority_color
        : "#f3f4f6",
      status: location.state ? location.state.item.status : "",
      ispublic: location.state ? location.state.item.ispublic : "",
      admin_note: location.state ? location.state.item.admin_note : "",
      is_default: location.state ? location.state.item.is_default : 0,
    },
    validationSchema: Yup.object({
      priority: Yup.string().required("Priority is required"),
      priority_desc: Yup.string().required("Priority Description is required"),
      priority_color: Yup.string().required("Priority Color is required"),
      status: Yup.string().required("Status is required"),
      ispublic: Yup.string().required("Ispublic is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_ticketPriority,
          formData,
          { dispatch, redux: ticketPriorityReducers },
          "ADD_TICKETPRIORITY",
          () => {
            navigate("/administrator/manage/priorities");
          }
        );
      } else {
        putData(
          `${API_URL_ticketPriority}${location.state.item.id}/`,
          formData,
          { dispatch, redux: ticketPriorityReducers },
          "ADD_TICKETPRIORITY",
          () => {
            navigate("/administrator/manage/priorities");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Priority"}
        excludedPaths={["manage", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          {typeForm === "create" ? "Create" : "Edit"}
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Priority <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="priority"
                    id="priority"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.priority}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.priority && formik.errors.priority && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.priority}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Priority description <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="priority_desc"
                    id="priority_desc"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    value={formik.values.priority_desc}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.priority_desc &&
                    formik.errors.priority_desc && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.priority_desc}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Priority color <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    data-tooltip-id="color-tooltip"
                    data-tooltip-content="Please choose the color"
                    data-tooltip-place="top"
                    type="color"
                    name="priority_color"
                    id="priority_color"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    value={formik.values.priority_color}
                    className="block w-full rounded-md border border-gray-500 bg-white px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.priority_color &&
                    formik.errors.priority_color && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.priority_color}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7 grid grid-cols-12">
                <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap">
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="status"
                        type="radio"
                        name="status"
                        checked={formik.values.status === 1}
                        onChange={() => formik.setFieldValue("status", 1)}
                        ref={(ref) => (inputRefs.current[3] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2 mr-5">Active</span>
                    </label>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        type="radio"
                        id="status"
                        name="status"
                        checked={formik.values.status === 0}
                        onChange={() => formik.setFieldValue("status", 0)}
                        ref={(ref) => (inputRefs.current[4] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2">Inactive</span>
                    </label>
                  </div>
                  {formik.touched.status && formik.errors.status && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.status}
                    </div>
                  )}
                </div>

                <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Visibility <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap">
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="ispublic"
                        name="ispublic"
                        type="radio"
                        checked={formik.values.ispublic === 1}
                        onChange={() => formik.setFieldValue("ispublic", 1)}
                        ref={(ref) => (inputRefs.current[5] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2 mr-5">Public</span>
                    </label>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="ispublic"
                        name="ispublic"
                        type="radio"
                        checked={formik.values.ispublic === 0}
                        onChange={() => formik.setFieldValue("ispublic", 0)}
                        ref={(ref) => (inputRefs.current[6] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2">Private</span>
                    </label>
                  </div>
                  {formik.touched.ispublic && formik.errors.ispublic && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.ispublic}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 pb-5">
              <div className="mt-4 grid grid-cols-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                  Admin notes
                </label>
                <div>
                  <CKEditor
                    config={toolbarConfig}
                    type="classic"
                    initData={formik.values.admin_note}
                    onChange={(e) => {
                      formik.setFieldValue("admin_note", e.editor.getData());
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
        <Tooltip id="color-tooltip" />
      </div>
    </Container>
  );
};

export default FormPriorities;
